"use client";

import React, { useEffect, useState } from "react";
import { CircleQuestionMark as HelpCircle, Cpu, Wrench, Clock, PhoneCall, MapPin, Sparkles, Users, GraduationCap, ClipboardCheck, Trophy } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface FaqItem {
  id: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  questionVi: string;
  questionEn: string;
  answerVi: React.ReactNode;
  answerEn: React.ReactNode;
}
const FAQ_ITEMS: FaqItem[] = [{
  id: "item-1",
  icon: Cpu,
  questionVi: "Chương trình Robotics tại ROBOTO là gì?",
  questionEn: "What is the Robotics program at ROBOTO?",
  answerVi: "Lộ trình Robotics chuẩn hóa từ 4 đến 16 tuổi, gồm 2 giai đoạn chính: Duplo Foundation Track (D1-D4, 4-6 tuổi) và WhalesBot AI Module 1S Track (HP1-HP6, 7+ tuổi), tiến tới chuẩn bị thi đấu Enjoy AI cho nhóm nâng cao.",
  answerEn: "Standardized Robotics pathway ages 4-16, comprising 2 main stages: Duplo Foundation Track (D1-D4, ages 4-6) and WhalesBot AI Module 1S Track (HP1-HP6, ages 7+), advancing toward competition preparation (Enjoy AI) for advanced groups."
}, {
  id: "item-2",
  icon: Sparkles,
  questionVi: "Học phần Duplo và WhalesBot là gì?",
  questionEn: "What are Duplo and WhalesBot modules?",
  answerVi: "Duplo D1-D4 xây dựng nền tảng cơ học và tư duy không gian bằng bộ LEGO Education Duplo; WhalesBot HP1-HP6 phát triển lập trình điều khiển, cảm biến và robot tự hành bằng nền tảng WhalesBot AI Module 1S.",
  answerEn: "Duplo D1-D4 builds mechanical foundation and spatial reasoning using LEGO Education Duplo kits; WhalesBot HP1-HP6 develops control programming, sensor integration and autonomous robotics using WhalesBot AI Module 1S platform."
}, {
  id: "item-3",
  icon: Wrench,
  questionVi: "Học cụ nào được sử dụng trong lớp?",
  questionEn: "What equipment is used in class?",
  answerVi: "LEGO Education Duplo construction kit (giai đoạn nền tảng) và WhalesBot AI Module 1S Platform (giai đoạn nâng cao).",
  answerEn: "LEGO Education Duplo construction kit (foundation stage) and WhalesBot AI Module 1S Platform (advanced stage)."
}, {
  id: "item-4",
  icon: Users,
  questionVi: "Chương trình Robotics tại ROBOTO phù hợp với những đối tượng nào?",
  questionEn: "Who is the Robotics program at ROBOTO suitable for?",
  answerVi: "Chương trình được thiết kế cho trẻ từ 4–16 tuổi, cấu trúc từ người mới bắt đầu (Duplo Foundation Track, không yêu cầu nền tảng trước) đến học viên nâng cao (WhalesBot Track), duy trì tỉ lệ cố vấn 1:6 (1 giảng viên kèm tối đa 6 học viên) xuyên suốt lộ trình để bảo đảm chất lượng hướng dẫn sát sao.",
  answerEn: "The program is structured for children aged 4–16, welcoming complete beginners (Duplo Foundation Track, no prior experience required) through advancing students (WhalesBot Track), maintaining a strict 1:6 mentor-to-student ratio throughout for personalized, high-standard guidance."
}, {
  id: "item-5",
  icon: GraduationCap,
  questionVi: "Trẻ chưa từng học Robotics có thể bắt đầu không?",
  questionEn: "Can children with no prior robotics experience get started?",
  answerVi: "Có, hoàn toàn có thể. Giai đoạn Duplo Foundation Track (D1–D4, 4–6 tuổi) được xây dựng dành cho trẻ mới bắt đầu và không yêu cầu bất kỳ kinh nghiệm nào trước đó. Đối với học viên từ 7 tuổi muốn theo học thẳng WhalesBot HP1–HP6, trẻ cần hoàn thành chặng D4 hoặc vượt qua bài kiểm tra đánh giá năng lực đầu vào.",
  answerEn: "Yes, absolutely. The Duplo Foundation Track (D1–D4, ages 4–6) requires no prior experience whatsoever. For students aged 7+ seeking direct entry into WhalesBot HP1–HP6, they must complete D4 or pass an entrance assessment."
}, {
  id: "item-6",
  icon: Clock,
  questionVi: "Buổi học thử diễn ra như thế nào?",
  questionEn: "How is the trial session conducted?",
  answerVi: "Buổi trải nghiệm 60 phút có giáo viên hướng dẫn trực tiếp tại phòng Lab.",
  answerEn: "60-minute hands-on experience with direct instructor guidance in our lab room."
}, {
  id: "item-7",
  icon: PhoneCall,
  questionVi: "Làm sao để đăng ký tư vấn hoặc học thử?",
  questionEn: "How do I register for consultation or trial?",
  answerVi: <span>
        Điền form &ldquo;Đăng ký tư vấn&rdquo; trên site hoặc gọi hotline{" "}
        <a href="tel:0346386986" className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80">
          0346 386 986
        </a>
        ; đội ngũ ROBOTO sẽ liên hệ tư vấn lộ trình phù hợp.
      </span>,
  answerEn: <span>
        Fill the consultation registration form on our site or call hotline{" "}
        <a href="tel:0346386986" className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80">
          0346 386 986
        </a>
        ; ROBOTO team will contact you to recommend the suitable pathway.
      </span>
}, {
  id: "item-8",
  icon: ClipboardCheck,
  questionVi: "Phụ huynh có thể theo dõi quá trình học tập và sản phẩm của con như thế nào?",
  questionEn: "How can parents track their child's learning progress and project output?",
  answerVi: "Theo quy định học vụ tại ROBOTO, cứ mỗi 4 buổi học, phụ huynh sẽ nhận được một báo cáo tiến độ chi tiết đánh giá tiêu chí tư duy phản biện, kỹ năng xử lý lỗi (debugging), cùng với video ghi lại phần thuyết trình dự án của chính học viên.",
  answerEn: "Under ROBOTO academic policy, every 4 sessions parents receive a comprehensive progress report covering critical-thinking criteria, debugging skills, and a recorded video of the student's own project presentation."
}, {
  id: "item-9",
  icon: Trophy,
  questionVi: "Học Robotics có giúp trẻ chuẩn bị cho các cuộc thi Robotics hoặc STEM không?",
  questionEn: "Does learning Robotics help prepare children for Robotics or STEM competitions?",
  answerVi: "Có. Lộ trình đào tạo bao gồm chặng chuẩn bị thi đấu Enjoy AI và nhánh Competitive Robotics rèn luyện năng lực cho các giải đấu như FIRST LEGO League, World Robot Olympiad và VEX Robotics. Lưu ý: ROBOTO huấn luyện độc lập theo chuẩn quốc tế của các giải đấu này và không tuyên bố là đối tác đại diện chính thức.",
  answerEn: "Yes. The pathway includes an Enjoy AI competition-prep stage and Competitive Robotics track preparing students for tournaments such as FIRST LEGO League, World Robot Olympiad, and VEX Robotics. Note: ROBOTO trains independently to these tournament standards and does not claim to be an official representing partner."
}, {
  id: "item-10",
  icon: MapPin,
  questionVi: "Tôi có thể tìm hiểu thêm về cơ sở và liên hệ ở đâu?",
  questionEn: "Where can I find more information and contact details?",
  answerVi: <span>
        Xem danh sách cơ sở và bản đồ tại Footer của site, hoặc liên hệ qua email{" "}
        <a href="mailto:info@robo.edu.vn" className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80">
          info@robo.edu.vn
        </a>{" "}
        / hotline{" "}
        <a href="tel:0346386986" className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80">
          0346 386 986
        </a>
        .
      </span>,
  answerEn: <span>
        View our locations and map in the site Footer, or contact us via email{" "}
        <a href="mailto:info@robo.edu.vn" className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80">
          info@robo.edu.vn
        </a>{" "}
        / hotline{" "}
        <a href="tel:0346386986" className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80">
          0346 386 986
        </a>
        .
      </span>
}];
export default function FaqAccordion() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  useEffect(() => {
    const readLanguage = () => {
      try {
        const stored = localStorage.getItem("roboto_lang");
        if (stored === "en" || stored === "vi") {
          setLang(stored);
        }
      } catch {
        // Fallback to default language
      }
    };
    readLanguage();
    const handleCustomChange = () => readLanguage();
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang") {
        readLanguage();
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const isVi = lang === "vi";
  return <section id="faqaccordion" data-nav="light" className="relative overflow-hidden bg-background py-16 text-foreground md:py-24">
      <div ref={ref} className={`mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 transition-all duration-350 ease-[cubic-bezier(0.16,1,0.3,1)] ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
        {/* Header Block */}
        <div className="mx-auto mb-12 max-w-2xl text-center md:mb-16">
          <Badge variant="outline" className="mb-3.5 inline-flex border-secondary/40 bg-secondary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-secondary">
            <HelpCircle className="mr-1.5 size-3.5 text-secondary" />
            {isVi ? "HỎI ĐÁP & HỖ TRỢ" : "FAQ & SUPPORT"}
          </Badge>

          <h2 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            {isVi ? "Câu hỏi thường gặp" : "Frequently Asked Questions"}
          </h2>

          <div aria-hidden="true" className="mx-auto mt-4 h-0.5 w-16 rounded-full bg-secondary" />
        </div>

        {/* Focused Single-Column Accordion */}
        <div className="mx-auto max-w-3xl">
          <div className="rounded-xl border border-border bg-card p-2 shadow-sm sm:p-4">
            <Accordion type="single" collapsible className="w-full space-y-2">
              {FAQ_ITEMS.map((item, index) => {
              const IconComponent = item.icon;
              return <AccordionItem key={item.id} value={item.id} data-index={index} className="rounded-lg border border-border/60 bg-background/50 px-4 transition-colors data-[state=open]:border-secondary/50 data-[state=open]:bg-secondary/5">
                    <AccordionTrigger className="group py-4 text-left font-default text-base font-semibold text-foreground hover:no-underline sm:text-lg">
                      <div className="flex items-center gap-3.5 pr-2">
                        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/60 text-muted-foreground transition-colors group-hover:border-secondary/40 group-hover:bg-secondary/10 group-hover:text-secondary group-data-[state=open]:border-secondary group-data-[state=open]:bg-secondary group-data-[state=open]:text-secondary-foreground">
                          <IconComponent className="size-4.5" />
                        </span>
                        <span className="leading-snug transition-colors group-hover:text-primary group-data-[state=open]:text-foreground">
                          {isVi ? item.questionVi : item.questionEn}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="font-default text-sm leading-[1.65] text-muted-foreground sm:text-base">
                      <div className="pb-2 pt-1 pl-[3.125rem] text-foreground/90">
                        {isVi ? item.answerVi : item.answerEn}
                      </div>
                    </AccordionContent>
                  </AccordionItem>;
            })}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}